import { REQUIRED_MESSAGES } from '@srm/ui/lib/requiredMessage';
import '../ActivityActions.scss';
import React, { useEffect, useState } from 'react';
import { IFormValues } from '@models/Forms/IForms';
import { getABookFormValues } from '@utils/abookUtils';
import DueTimeModeTypeOption, { DueTimeModeType } from '@/types/DueTimeType';
import { IAddressBookData } from '@models/addressbook/IAddressBookData';
import {
    IRequestAdditionalApprovalModel,
    IRequestAdditionalApprovalSend,
} from '@models/document/activityExecutors/IRequestAdditionalApprovalModel';
import { ActivityExecutionService } from '@services/actions/ActivityExecutionService';
import { useForm } from 'react-hook-form';
import { IOption } from '@srm/ui/types/IOption';
import DurationSpanTypeOption from '@/types/DurationSpanType';
import ModalContainer, { IModalContainerProps } from '@molecules/ModalContainer/ModalContainer';
import AbookpickerControl from '@controls/AbookpickerControl';
import FormControl from '@srm/ui/controls/FormControl';
import InputNumberControl from '@srm/ui/controls/InputNumberControl';
import SelectControl from '@srm/ui/controls/SelectControl';
import DateFieldControl from '@srm/ui/controls/DateFieldControl';
import TextareaControl from '@srm/ui/controls/TextareaControl';
import Select from '@srm/ui/select';
import { requiredTextRules } from '@srm/ui/lib/requiredTextRules';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';
import { IRequestAddApprovalProps } from './IRequestAddApprovalProps';
import { IRequestAddApprovalFormData } from './IRequestAddApprovalFormData';
import { useDocumentAbookTabs } from '@/hooks/useDocumentAbookTabs';

export const RequestAddApprovalModal: React.FC<IRequestAddApprovalProps> = (props: IRequestAddApprovalProps) => {
    const [formValues, setFormValues] = useState<string>('{}');
    const activated = React.useRef(false);

    const getFormValues = async (fValues?: IFormValues) => {
        const vals = await getABookFormValues(props.docId, fValues);
        if (activated.current && vals) {
            setFormValues(vals);
        }
    };

    const [periodType, setPeriodType] = useState<DueTimeModeType>(DueTimeModeType.Indefinite);
    const [executors, setExecutors] = useState<IAddressBookData[]>();

    const [model, setModel] = useState<IRequestAdditionalApprovalModel>();
    const abookTabs = useDocumentAbookTabs(props.docId, model?.tabs);
    const [errorText, setErrorText] = useState<string>();
    // Подготовка (модель действия) и отправка — разные состояния: подготовку можно бросить
    // закрытием окна, отправку — нет.
    const [loading, setLoading] = useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const serv = new ActivityExecutionService();

    const formMethods = useForm<IRequestAddApprovalFormData>({
        mode: 'onBlur', // "onChange"
        defaultValues: { periodType: DueTimeModeType[DueTimeModeType.Indefinite] },
    });
    const hasUnsavedInput = useUnsavedFormInput(formMethods, { textFields: ['taskText'] });

    useEffect(() => {
        formMethods.register('periodType', { required: REQUIRED_MESSAGES.select });
        // синхронизируем дефолт (Бессрочно) в значение RHF-поля, иначе required заблокирует submit
        formMethods.setValue('periodType', DueTimeModeType[periodType]);
    }, []);

    const updatePeriodType = (type: DueTimeModeType) => {
        setPeriodType(type);
        formMethods.setValue('periodType', DueTimeModeType[type], { shouldValidate: true, shouldDirty: true });
    };

    const onSubmit = (form: IRequestAddApprovalFormData) => {
        const data: IRequestAdditionalApprovalSend = {
            taskText: form.taskText,
            dueTimeMode: DueTimeModeType[periodType],
            executorsIds: executors?.map((x) => x.key) ?? [],
            periodDate: form.periodDate,
            termCount: form.termCount,
            termType: form?.termType ? form?.termType[0].value : undefined,
            activityKeys: [Number(props.activityId)],
            finishedNotificationText: model?.finishedNotificationText ?? '',
        };

        setIsSubmitting(true);
        return serv
            .sendRequestAdditionalApproval(props.actionKey, props.activityId, data)
            .then(() => {
                if (props.completeHandler) props.completeHandler(true);
            })
            .catch((err) => {
                setErrorText(err);
            })
            .finally(() => setIsSubmitting(false));
    };

    const onPeriodTypeChange = (option: IOption[]) => {
        if (option.length == 0) return;
        const type = DueTimeModeType[option[0].value as keyof typeof DueTimeModeType];
        updatePeriodType(type);
    };

    useEffect(() => {
        // Закрытое окно ответ подготовки не применяет.
        let ignore = false;
        setLoading(true);
        activated.current = true;

        serv.getRequestAdditionalApprovalModel(props.actionKey, props.activityId)
            .then((res) => {
                if (ignore) return;
                setModel(res.data);
                if (res.data.defaultDueTime) {
                    const type = DueTimeModeType[res.data.defaultDueTime.type];
                    setPeriodType(type);
                    // periodType обязателен и валидируется через handleSubmit — кладём его прямо
                    // в reset, иначе reset затрёт значение в undefined и сабмит заблокируется.
                    formMethods.reset({
                        termType: DurationSpanTypeOption.filter(
                            (x) => x.value === res.data.defaultDueTime.timeSpanType,
                        ),
                        periodType: DueTimeModeType[type],
                    });
                }
                void getFormValues(res.data?.formValues);
            })
            .catch((err) => {
                if (!ignore) setErrorText(err);
            })
            .finally(() => {
                if (!ignore) setLoading(false);
            });
        return () => {
            ignore = true;
            activated.current = false;
        };
    }, []);

    const modalContainerProps: IModalContainerProps = {
        header: 'Запрос дополнительного согласования',
        size: props.modalSize,
        showOkButton: true,
        showCancelButton: true,
        okButtonText: props.okButtonText,
        cancelButtonText: props.cancelButtonText,
        loading: loading || isSubmitting,
        closeDisabled: isSubmitting,
        hasUnsavedInput,
        errorText: errorText,
        formMethods: formMethods,
        onSubmit: onSubmit,
        onCancel: () => {
            if (props?.completeHandler) props?.completeHandler(false);
        },
    };

    return (
        <ModalContainer {...modalContainerProps}>
            <AbookpickerControl
                label="Исполнители"
                placeholder="Исполнители"
                isMultiple={true}
                title="Исполнители"
                displayValue={executors?.map((x) => x.fullName)?.join(',') ?? ''}
                formValues={formValues}
                name="executors"
                tabsSettings={abookTabs}
                externalSearch={model?.externalSearch}
                required
                rules={{ required: REQUIRED_MESSAGES.multiple }}
                showChips={false}
                control={formMethods.control}
                onSetValues={(value: IAddressBookData[]) => {
                    setExecutors(value);
                }}
            />

            <FormControl name="periodType" label="Срок исполнения" control={formMethods.control} required>
                <Select
                    placeholder="Укажите срок исполнения"
                    values={DueTimeModeTypeOption.filter((x) => x.value === DueTimeModeType[periodType])}
                    multiselect={false}
                    options={DueTimeModeTypeOption}
                    loading={loading}
                    readOnly
                    onChange={onPeriodTypeChange}
                />
            </FormControl>

            {periodType == DueTimeModeType.TimeSpan && (
                <>
                    <InputNumberControl
                        name="termCount"
                        defaultValue={model?.defaultDueTime?.timeSpanValue ?? 1}
                        min={1}
                        max={9999}
                        floatPoints={0}
                        control={formMethods.control}
                    />

                    <SelectControl
                        multiselect={false}
                        name="termType"
                        readOnly
                        options={DurationSpanTypeOption}
                        /*defaultValue={[
                            DurationSpanTypeOption.find(
                                (option) => option.value === model?.defaultDueTime?.timeSpanType,
                            ) ?? DurationSpanTypeOption[2],
                        ]}*/
                        loading={loading}
                        control={formMethods.control}
                    />
                </>
            )}

            {periodType == DueTimeModeType.Date && <DateFieldControl name="periodDate" control={formMethods.control} />}

            <TextareaControl
                label="Текст задачи"
                readOnly={false}
                required={true}
                rules={requiredTextRules()}
                name="taskText"
                defaultValue={model?.prefilledComment}
                control={formMethods.control}
            />
        </ModalContainer>
    );
};
